// Updated version of ProfileMe.jsx
// Handles 404 (profile not found) gracefully for new roles like MO, Lab Tech, etc.
// Instead of redirecting to login, it initializes an empty profile.

import React, { useEffect, useMemo, useState } from 'react';
import { GlobalStyles, Button } from '@mui/material';
// Adjust import paths to stay within the src directory. This file is under
// src/pages/profile, so go up two levels to reach src then import.
import profileApi, { backfillProfilesAdmin } from '../../services/profileApi';
import http from '../../services/http';
import { internApi } from '../../services/internApi';
import ProfileCard from '../../components/profile/ProfileCard';
import ProfileForm from '../../components/profile/ProfileForm';




function isoDate(d) {
  try {
    return new Date(d).toISOString();
  } catch {
    return '';
  }
}
function ymd(d) {
  try {
    const dt = new Date(d);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const da = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${da}`;
  } catch {
    return '';
  }
}
function prettyDate(d) {
  try {
    const dt = new Date(d);
    return dt.toLocaleString();
  } catch {
    return String(d || '');
  }
}

function computeCompleteness(p) {
  if (!p) return { score: 0, missing: ['Display name', 'Designation', 'Centre'] };
  const checks = [
    { key: 'displayName', label: 'Display name' },
    { key: 'designation', label: 'Designation' },
    { key: 'centres', label: 'Centre', test: (v) => (Array.isArray(v) ? v.length > 0 : !!v) },
    { key: 'qualifications', label: 'Qualifications', test: (v) => (Array.isArray(v) ? v.length > 0 : !!v) },
    { key: 'registrationNumber', label: 'Registration number' },
    { key: 'photoURL', label: 'Photo' },
  ];
  let ok = 0;
  const missing = [];
  for (const c of checks) {
    const v = p[c.key];
    const pass = c.test ? c.test(v) : (v != null && String(v).trim() !== '');
    if (pass) ok += 1;
    else missing.push(c.label);
  }
  const score = Math.round((ok / checks.length) * 100);
  return { score, missing: missing.slice(0, 4) };
}

function readLoggedInUser() {
  try {
    return JSON.parse(localStorage.getItem('loggedInUser') || 'null');
  } catch {
    return null;
  }
}
function writeLoggedInUser(patch) {
  try {
    const cur = readLoggedInUser() || {};
    const merged = {
      ...cur,
      name: patch.displayName ?? cur.name ?? cur?.user?.name,
      role: patch.designation ?? cur.role ?? cur?.user?.role,
      userType: patch.designation ?? cur.userType,
      user: {
        ...(cur.user || {}),
        name: patch.displayName ?? cur?.user?.name,
        role: patch.designation ?? cur?.user?.role,
      },
    };
    localStorage.setItem('loggedInUser', JSON.stringify(merged));
  } catch {}
}

// Dark-aware form surfaces
function DarkFormStyles() {
  return (
    <GlobalStyles
      styles={(theme) => ({
        '.MuiInputBase-root': {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? theme.palette.background.paper
              : theme.palette.common.white,
          color: theme.palette.text.primary,
        },
        '.MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
        '.MuiInputBase-input::placeholder': { color: theme.palette.text.disabled, opacity: 1 },
        input: {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? theme.palette.background.paper
              : theme.palette.common.white,
          color: theme.palette.text.primary,
          borderColor: theme.palette.divider,
        },
        textarea: {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? theme.palette.background.paper
              : theme.palette.common.white,
          color: theme.palette.text.primary,
          borderColor: theme.palette.divider,
        },
        'input::placeholder, textarea::placeholder': { color: theme.palette.text.disabled, opacity: 1 },
      })}
    />
  );
}

// Normalize incoming record to a single shape (no schema changes, just mapping)
function normalizeIncoming(p = {}) {
  const out = { ...p };
  out.goalsShortTerm =
    out.goalsShortTerm ??
    out.shortTermGoal ??
    out?.goals?.shortTerm ??
    out.short_goal ??
    out.shortterm ??
    out.shortTerm ??
    '';
  out.goalsLongTerm =
    out.goalsLongTerm ??
    out.longTermGoal ??
    out?.goals?.longTerm ??
    out.long_goal ??
    out.longterm ??
    out.longTerm ??
    '';
  if (typeof out.areasOfInterest === 'string') {
    out.areasOfInterest = out.areasOfInterest.split(',').map((s) => s.trim()).filter(Boolean);
  }
  if (typeof out.languagePrefs === 'string') {
    out.languagePrefs = out.languagePrefs.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return out;
}

// Normalize payload before save
function normalizeOutgoing(form) {
  const out = { ...form };
  if (out.roleLevel === '' || out.roleLevel == null) delete out.roleLevel;
  else out.roleLevel = Number(out.roleLevel);
  if (typeof out.superviseeUserIds === 'string') {
    out.superviseeUserIds = out.superviseeUserIds
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (out.goalsShortTerm == null) out.goalsShortTerm = '';
  if (out.goalsLongTerm == null) out.goalsLongTerm = '';
  if (typeof out.areasOfInterest === 'string') {
    out.areasOfInterest = out.areasOfInterest
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return out;
}

export default function ProfileMe() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillMsg, setBackfillMsg] = useState('');
  const [kpiBusy, setKpiBusy] = useState(false);
  const [kpi, setKpi] = useState({ visits: 0, pending: 0, activities: 0, reviewerQueue: 0 });
  const [timelineBusy, setTimelineBusy] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [actions, setActions] = useState([]);
  const [editMode, setEditMode] = useState(false);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const res = await profileApi.getMe();
      let p = normalizeIncoming(res?.profile || res || {});
      const u = readLoggedInUser() || {};
      const uName = u?.name || u?.user?.name;
      const uRole = u?.role || u?.userType || u?.user?.role;
      if (!p.displayName && uName) p.displayName = uName;
      if (!p.designation && uRole) p.designation = uRole;
      // Ensure array fields exist to avoid undefined.join errors
      if (!Array.isArray(p.departments)) p.departments = [];
      if (!Array.isArray(p.centres)) p.centres = [];
      if (!Array.isArray(p.qualifications)) p.qualifications = [];
      if (!Array.isArray(p.areasOfInterest)) p.areasOfInterest = [];
      if (!Array.isArray(p.languagePrefs)) p.languagePrefs = [];
      if (!Array.isArray(p.superviseeUserIds)) p.superviseeUserIds = [];
      if (!Array.isArray(p.permissions)) p.permissions = [];
      setProfile(p);
    } catch (e) {
      // Treat 404 as a new profile instead of an error
      if (e?.response?.status === 404) {
        setProfile({
          displayName: '',
          designation: readLoggedInUser()?.role || '',
          departments: [],
          centres: [],
          qualifications: [],
          areasOfInterest: [],
          languagePrefs: [],
          superviseeUserIds: [],
          permissions: [],
        });
        setError('');
      } else if (e?.response?.status === 401 || /expired token/i.test(String(e?.message))) {
        setTimeout(() => {
          window.location.href = '/cmfm-login';
        }, 1200);
      } else {
        setError(e?.message || 'Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // intentionally not including dependencies to mimic original behaviour
  }, []);

  async function handleSave(form) {
    setSaving(true);
    setError('');
    try {
      const payload = normalizeOutgoing(form);
      const res = await profileApi.saveMe(payload);
      const updated = normalizeIncoming(res?.profile || res || {});
      setProfile(updated);
      setEditMode(false);
      const displayName = updated.displayName || '';
      const designation = updated.designation || '';
      window.dispatchEvent(
        new CustomEvent('profile:updated', {
          detail: { displayName, designation },
        }),
      );
      if (displayName || designation) writeLoggedInUser({ displayName, designation });
    } catch (e) {
      setError(e?.message || 'Failed to save profile');
      if (e?.response?.status === 401) {
        setTimeout(() => {
          window.location.href = '/cmfm-login';
        }, 1200);
      }
    } finally {
      setSaving(false);
    }
  }

  
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('loggedInUser') || 'null');
    } catch {
      return null;
    }
  }, []);

  const isAdminLike = useMemo(() => {
    const r = String(user?.role || '').toLowerCase();
    return r.includes('admin') || r.includes('faculty') || r.includes('hod') || r.includes('dean') || r.includes('head');
  }, [user?.role]);

  const { score: profileCompleteness, missing: missingFields } = useMemo(
    () => computeCompleteness(profile),
    [profile]
  );

  const handleBackfill = async () => {
    if (!isAdminLike || backfillBusy) return;
    setBackfillMsg('');
    try {
      setBackfillBusy(true);
      const r = await backfillProfilesAdmin();
      const created = r?.created ?? r?.data?.created ?? 0;
      const skipped = r?.skipped ?? r?.data?.skipped ?? 0;
      const msg = r?.message || r?.data?.message || `Backfill completed. Created: ${created}, Skipped: ${skipped}`;
      setBackfillMsg(msg);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Backfill failed';
      setBackfillMsg(msg);
    } finally {
      setBackfillBusy(false);
    }
  };


  async function loadKPIs() {
    if (kpiBusy) return;
    try {
      setKpiBusy(true);
      const now = new Date();
      const from = ymd(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)); // last 30 days
      const to = ymd(now);

      // Visits count (mine) for last 30 days
      const vCountRes = await http.get(`/api/visits?mine=1&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&count=1`);
      const visits = vCountRes?.data?.count ?? vCountRes?.data?.total ?? 0;

      // Pending approvals (mine) - endpoint returns visits array
      const pendingRes = await http.get(`/api/visits/pending?mine=1&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&limit=200`);
      const pending = Array.isArray(pendingRes?.data?.visits) ? pendingRes.data.visits.length : 0;

      // Intern activities (mine) - list endpoint returns {success,..., activities/items}
      const actRes = await internApi.listActivitiesMine({ limit: 200 });
      const activitiesArr =
        actRes?.activities ||
        actRes?.items ||
        actRes?.data?.activities ||
        actRes?.data?.items ||
        [];
      const activities = Array.isArray(activitiesArr) ? activitiesArr.length : 0;

      // Reviewer queue (for JR/SR/Faculty) - safe fallback
      let reviewerQueue = 0;
      try {
        const rqRes = await internApi.reviewerQueue({ limit: 200 });
        const rqArr =
          rqRes?.items ||
          rqRes?.queue ||
          rqRes?.data?.items ||
          rqRes?.data?.queue ||
          [];
        reviewerQueue = Array.isArray(rqArr) ? rqArr.length : 0;
      } catch {}

      setKpi({ visits, pending, activities, reviewerQueue });

      // Actions rail (simple, demo-ready)
      const next = [];
      if (pending > 0) next.push({ label: 'Pending prescription approvals', value: pending, hint: 'Visit Approvals' });
      if (reviewerQueue > 0) next.push({ label: 'Intern activities to review', value: reviewerQueue, hint: 'Reviewer Queue' });
      if (profileCompleteness < 100) next.push({ label: 'Complete your profile', value: `${profileCompleteness}%`, hint: 'Edit Profile' });
      setActions(next);
    } catch (e) {
      // keep silent; KPIs are additive for demo
      // eslint-disable-next-line no-console
      console.warn('Profile KPIs load failed', e?.message || e);
    } finally {
      setKpiBusy(false);
    }
  }

  async function loadTimeline() {
    if (timelineBusy) return;
    try {
      setTimelineBusy(true);
      const now = new Date();
      const from = ymd(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000));
      const to = ymd(now);

      const vRes = await http.get(`/api/visits?mine=1&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&limit=20`);
      const visitsArr = Array.isArray(vRes?.data?.visits) ? vRes.data.visits : [];

      const actRes = await internApi.listActivitiesMine({ limit: 20 });
      const activitiesArr =
        actRes?.activities ||
        actRes?.items ||
        actRes?.data?.activities ||
        actRes?.data?.items ||
        [];
      const acts = Array.isArray(activitiesArr) ? activitiesArr : [];

      const items = [];

      for (const v of visitsArr) {
        items.push({
          type: 'visit',
          ts: v?.visitDate || v?.createdAt || v?.updatedAt,
          title: `OPD Visit • ${v?.centre || v?.chc || 'Centre'}`,
          subtitle: v?.citizenId ? `Citizen: ${v.citizenId}` : (v?.citizen ? `Citizen: ${v.citizen}` : (v?.visitId ? `Visit: ${v.visitId}` : '')),
          status: v?.prescription?.status || v?.status || '',
        });
      }

      for (const a of acts) {
        items.push({
          type: 'activity',
          ts: a?.updatedAt || a?.createdAt,
          title: `Intern Activity • ${a?.activityName || a?.title || a?.type || 'Activity'}`,
          subtitle: a?.centre ? `Centre: ${a.centre}` : (a?.posting ? `Posting: ${a.posting}` : ''),
          status: a?.status || '',
        });
      }

      items.sort((x, y) => (new Date(y.ts || 0).getTime() - new Date(x.ts || 0).getTime()));
      setTimeline(items.slice(0, 20));
    } catch (e) {
      console.warn('Profile timeline load failed', e?.message || e);
    } finally {
      setTimelineBusy(false);
    }
  }
return (
    <div className="p-4 space-y-6 cmfm-profile">
      <DarkFormStyles />
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">My Profile / मेरी प्रोफ़ाइल</h1>
          <p className="text-sm opacity-70">Update your profile details. / अपनी प्रोफ़ाइल विवरण अपडेट करें।</p>
        </div>
        {!loading && !error && (
          editMode ? (
            <div className="flex gap-2">
              <Button variant="outlined" onClick={() => setEditMode(false)} disabled={!!saving}>
                Cancel
              </Button>
              <Button form="profile-edit-form" type="submit" variant="contained" disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          ) : (
            <Button variant="contained" onClick={() => setEditMode(true)}>
              Edit
            </Button>
          )
        )}
      </div>
      {loading ? (
        <div className="text-sm opacity-70">Loading…</div>
      ) : error ? (
        <div className="text-red-600 dark:text-red-400">{error}</div>
      ) : (
        <>
          
          {/* Profile summary banner (no regression) */}
          <div className="mb-4 rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <div className="text-sm font-semibold">Profile completeness: {profileCompleteness}%</div>
                {missingFields?.length ? (
                  <div className="text-xs opacity-75 mt-1">Missing: {missingFields.join(', ')}</div>
                ) : (
                  <div className="text-xs opacity-75 mt-1">All key profile fields are complete.</div>
                )}
              </div>
              {isAdminLike ? (
                <div className="flex flex-col items-stretch gap-2">
                  <button
                    type="button"
                    onClick={handleBackfill}
                    disabled={backfillBusy}
                    className={`px-4 py-2 rounded-xl font-semibold border ${
                      backfillBusy
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-purple-50 text-purple-900 border-purple-200 hover:bg-purple-100 dark:bg-purple-900/20 dark:text-purple-100 dark:border-purple-800 dark:hover:bg-purple-900/30'
                    }`}
                    title="Creates missing profiles for existing users (does not modify existing profiles)"
                  >
                    {backfillBusy ? 'Backfilling…' : 'Backfill missing profiles (Admin)'}
                  </button>
                  {backfillMsg ? (
                    <div className="text-xs px-3 py-2 rounded border border-purple-200 bg-purple-50 text-purple-900 dark:border-purple-800 dark:bg-purple-900/20 dark:text-purple-100">
                      {backfillMsg}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>


          {/* Work dashboard (Stage 2) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <div className="lg:col-span-2 rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold">Your work (last 30 days)</div>
                  <div className="text-xs opacity-70 mt-0.5">Auto-generated from Visits + Intern Activities.</div>
                </div>
                <button
                  type="button"
                  onClick={() => { loadKPIs(); loadTimeline(); }}
                  disabled={kpiBusy || timelineBusy}
                  className={`px-3 py-2 rounded-xl text-sm font-semibold border ${
                    (kpiBusy || timelineBusy)
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-white hover:bg-gray-50 text-purple-900 border-purple-200 dark:bg-neutral-800 dark:hover:bg-neutral-700 dark:text-neutral-100 dark:border-neutral-700'
                  }`}
                >
                  {(kpiBusy || timelineBusy) ? 'Refreshing…' : 'Refresh'}
                </button>
              </div>

              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded-xl p-3 bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700">
                  <div className="text-[11px] uppercase tracking-wide opacity-70">My Visits</div>
                  <div className="text-xl font-semibold mt-1">{kpi.visits}</div>
                </div>
                <div className="rounded-xl p-3 bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700">
                  <div className="text-[11px] uppercase tracking-wide opacity-70">Pending Approvals</div>
                  <div className="text-xl font-semibold mt-1">{kpi.pending}</div>
                </div>
                <div className="rounded-xl p-3 bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700">
                  <div className="text-[11px] uppercase tracking-wide opacity-70">Activities</div>
                  <div className="text-xl font-semibold mt-1">{kpi.activities}</div>
                </div>
                <div className="rounded-xl p-3 bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700">
                  <div className="text-[11px] uppercase tracking-wide opacity-70">To Review</div>
                  <div className="text-xl font-semibold mt-1">{kpi.reviewerQueue}</div>
                </div>
              </div>

              <div className="mt-4">
                <div className="text-sm font-semibold mb-2">Recent timeline</div>
                {timelineBusy ? (
                  <div className="text-sm opacity-70">Loading timeline…</div>
                ) : timeline.length ? (
                  <div className="space-y-2">
                    {timeline.map((it, idx) => (
                      <div key={idx} className="flex items-start justify-between gap-3 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-3">
                        <div>
                          <div className="text-sm font-semibold">{it.title}</div>
                          {it.subtitle ? <div className="text-xs opacity-70 mt-0.5">{it.subtitle}</div> : null}
                          {it.status ? <div className="text-xs opacity-70 mt-0.5">Status: {it.status}</div> : null}
                        </div>
                        <div className="text-xs opacity-60 whitespace-nowrap">{prettyDate(it.ts)}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm opacity-70">No recent activity found.</div>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4">
              <div className="text-sm font-semibold">Next actions</div>
              <div className="text-xs opacity-70 mt-0.5">Auto-suggested based on pending work.</div>

              <div className="mt-3 space-y-2">
                {actions?.length ? (
                  actions.map((a, idx) => (
                    <div key={idx} className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-800 p-3">
                      <div className="text-sm font-semibold">{a.label}</div>
                      <div className="text-xs opacity-70 mt-0.5">
                        {String(a.value)}{a.hint ? ` • ${a.hint}` : ''}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-sm opacity-70">No pending actions detected.</div>
                )}
              </div>
            </div>
          </div>

<ProfileCard profile={profile} />
          {editMode && (
            <div className="rounded-xl shadow-md p-4 bg-white dark:bg-neutral-900 dark:text-neutral-100">
              <ProfileForm initial={profile} onSave={handleSave} saving={saving} id="profile-edit-form" />
            </div>
          )}
        </>
      )}
    </div>
  );
}
